/**
 * The step list itself — one renderer, two mounts.
 *
 * Extracted from `cowork/cowork-context-panel.tsx` in spec 084 step 1 so the
 * CoWork side panel and the chat-universal card below the messages cannot drift
 * into two different-looking checklists.
 *
 * @module components/chat/plan/plan-timeline
 */

import { Check } from "lucide-react";

import { cn } from "@/lib/utils";
import type { PlanStep } from "@/types/plan";

/**
 * Vertical timeline of plan steps. Completed nodes fill green with a check and a
 * solid trail; the in-progress node is a pulsing ring ("you are here"); pending
 * nodes are hollow with a faint trail ahead.
 */
export function PlanTimeline({ tasks }: { tasks: PlanStep[] }) {
  return (
    <div className="flex flex-col">
      {tasks.map((task, i) => {
        const done = task.status === "completed";
        const active = task.status === "in_progress";
        const last = i === tasks.length - 1;
        return (
          <div key={`${i}-${task.title}`} data-plan-active={active ? "true" : undefined} className="flex gap-3">
            {/* rail: node + connector to the next step */}
            <div className="flex flex-col items-center self-stretch">
              <span
                className={cn(
                  "z-[1] grid size-[18px] shrink-0 place-items-center rounded-full border-2 transition-colors",
                  done
                    ? "border-success bg-success"
                    : active
                      ? "border-secondary bg-secondary/10 ring-4 ring-secondary/10"
                      : "border-muted-foreground/25 bg-background",
                )}
              >
                {done ? (
                  <Check className="size-3 text-white" strokeWidth={3} />
                ) : active ? (
                  <span className="size-2 animate-pulse rounded-full bg-secondary" />
                ) : null}
              </span>
              {!last && <span className={cn("w-0.5 flex-1", done ? "bg-success/40" : "bg-border")} />}
            </div>
            {/* step title */}
            <span
              className={cn(
                "pt-px text-[13px] leading-snug transition-colors",
                last ? "pb-1" : "pb-4",
                done ? "text-muted-foreground" : active ? "font-semibold text-foreground" : "text-muted-foreground/80",
              )}
            >
              {task.title}
            </span>
          </div>
        );
      })}
    </div>
  );
}
